import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

/**
 * The Events service's public contract.
 *
 * A registration references a person by id and nothing more. Names are
 * resolved through People's read port, so widening a Person never widens
 * what Events can see.
 */

export const EVENT_STATUSES = ['DRAFT', 'PUBLISHED', 'CANCELLED', 'COMPLETED'] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

export const REGISTRATION_STATUSES = ['REGISTERED', 'WAITLISTED', 'CANCELLED'] as const;
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number];

// --- Requests ---------------------------------------------------------------

const eventNameField = z.string().trim().min(1).max(150);

export const createEventRequestSchema = z
  .object({
    name: eventNameField,
    description: z.string().trim().max(2000).nullish(),
    category: z.string().trim().max(100).nullish(),
    location: z.string().trim().max(200).nullish(),
    startAt: z.iso.datetime(),
    endAt: z.iso.datetime(),
    campusId: z.uuid().nullish(),
    organizationId: z.uuid().nullish(),
    /** Null means no limit. Zero would mean an event nobody can join. */
    capacity: z.number().int().positive().nullish(),
    registrationRequired: z.boolean().default(false),
  })
  .refine((value) => new Date(value.endAt) > new Date(value.startAt), {
    message: 'endAt must be after startAt',
    path: ['endAt'],
  });
export type CreateEventRequest = z.infer<typeof createEventRequestSchema>;

/**
 * Declared field by field rather than as `createEventRequestSchema.partial()`.
 *
 * `.partial()` keeps `.default()` values, so an empty PATCH would parse to a
 * body full of defaults and quietly overwrite real data with them.
 */
export const updateEventRequestSchema = z
  .object({
    name: eventNameField.optional(),
    description: z.string().trim().max(2000).nullish(),
    category: z.string().trim().max(100).nullish(),
    location: z.string().trim().max(200).nullish(),
    startAt: z.iso.datetime().optional(),
    endAt: z.iso.datetime().optional(),
    campusId: z.uuid().nullish(),
    organizationId: z.uuid().nullish(),
    capacity: z.number().int().positive().nullish(),
    registrationRequired: z.boolean().optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateEventRequest = z.infer<typeof updateEventRequestSchema>;

export const archiveEventRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type ArchiveEventRequest = z.infer<typeof archiveEventRequestSchema>;

export const cancelEventRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type CancelEventRequest = z.infer<typeof cancelEventRequestSchema>;

export const registerForEventRequestSchema = z.object({
  personId: z.uuid(),
});
export type RegisterForEventRequest = z.infer<typeof registerForEventRequestSchema>;

/**
 * A reason is optional here, unlike cancelling the event itself.
 *
 * Someone backing out of an event is ordinary and usually has no story worth
 * recording; cancelling the event is a decision someone will be asked about.
 */
export const cancelRegistrationRequestSchema = z.object({
  reason: z.string().trim().max(500).nullish(),
});
export type CancelRegistrationRequest = z.infer<typeof cancelRegistrationRequestSchema>;

export const listEventsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).nullish(),
  status: z.enum(EVENT_STATUSES).nullish(),
  campusId: z.uuid().nullish(),
  includeArchived: queryBoolean().default(false),
});
export type ListEventsQuery = z.infer<typeof listEventsQuerySchema>;

export const listRegistrationsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  status: z.enum(REGISTRATION_STATUSES).nullish(),
});
export type ListRegistrationsQuery = z.infer<typeof listRegistrationsQuerySchema>;

// --- Responses --------------------------------------------------------------

export interface EventSummaryResponse {
  readonly id: string;
  readonly name: string;
  readonly status: EventStatus;
  readonly campusId: string | null;
  readonly category: string | null;
  readonly startAt: string;
  readonly endAt: string;
  readonly capacity: number | null;
  /** Active (non-cancelled) registrations only. */
  readonly registeredCount: number;
  readonly archivedAt: string | null;
}

export interface EventDetailResponse extends EventSummaryResponse {
  readonly description: string | null;
  readonly organizationId: string | null;
  readonly location: string | null;
  readonly registrationRequired: boolean;
  readonly waitlistedCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * `displayName` is resolved through PeopleReadPort at read time rather than
 * copied into the registration row. A denormalised name is wrong the moment
 * someone marries, and a roster showing a former name is the kind of error a
 * congregation notices.
 */
export interface RegistrationResponse {
  readonly id: string;
  readonly personId: string;
  readonly displayName: string;
  readonly status: RegistrationStatus;
  readonly registeredAt: string;
}

/**
 * What `EventsReadPort` hands another module: enough to label a reference
 * and know whether it is still usable, not the registration list or the
 * schedule. See `PersonReference` and `GroupReference` for the same
 * reasoning applied to People and Groups.
 */
export interface EventReference {
  readonly id: string;
  readonly name: string;
  readonly isArchived: boolean;
}
